<h2 mat-dialog-title>{{ isSubscriptionLevel ? "Subscription Connection" : "Connection Info" }}</h2>
<div mat-dialog-content>
  <div *ngIf="loading"><i class="fas fa-spinner fa-pulse"></i> Loading...</div>
  <mat-error *ngIf="errorMessage">{{ errorMessage }}</mat-error>

  <ng-container *ngIf="info">
    <p class="text-muted" *ngIf="info.mayStillBePropagating">
      New credentials and access can take a few minutes to become active. If a connection fails right after creating
      or regenerating, wait a few minutes and try again.
    </p>

    <mat-tab-group>
      <mat-tab label="AI Agent (MCP)">
        <div style="margin-top: 10px">
          <p *ngIf="info.mcpServerUrl">
            Add a custom connector to your AI assistant with this URL. When asked to sign in, enter the Client ID and
            Client Secret shown below.
          </p>
          <p *ngIf="!info.mcpServerUrl" class="text-muted">The AI agent connection is not available in this environment.</p>
          <table class="table table-sm" *ngIf="info.mcpServerUrl">
            <tr>
              <th style="width: 30%">MCP server URL</th>
              <td>{{ info.mcpServerUrl }} <a href="#" (click)="copy(info.mcpServerUrl); false"><span class="fas fa-copy"></span></a></td>
            </tr>
            <tr><th>Client (application) ID</th><td>{{ info.clientId }} <a href="#" (click)="copy(info.clientId); false"><span class="fas fa-copy"></span></a></td></tr>
            <tr>
              <th>Client secret</th>
              <td>
                {{ secretDisplay }}
                <a href="#" (click)="showSecret = !showSecret; false"><span class="fas" [ngClass]="showSecret ? 'fa-eye-slash' : 'fa-eye'" title="Show or hide"></span></a>&nbsp;
                <a href="#" (click)="copy(info.clientSecret); false"><span class="fas fa-copy" title="Copy"></span></a>&nbsp;
                <a href="#" (click)="rotateSecret(); false"><span class="fas fa-sync" title="Rotate secret"></span></a>
                <div class="text-muted" *ngIf="info.clientSecretExpiresUtc">Expires {{ info.clientSecretExpiresUtc + "Z" | date: "medium" }}</div>
              </td>
            </tr>
          </table>
          <p class="text-muted" *ngIf="isSubscriptionLevel">
            With this subscription-level connection the assistant chooses which of your Agent Data Models to query.
          </p>
        </div>
      </mat-tab>

      <mat-tab label="SQL Connection">
        <div style="margin-top: 10px">
          <p>
            Connect any SQL client (SQL Server Management Studio, Azure Data Studio, Power BI, Excel, Python, ...) with
            <b>Microsoft Entra service principal</b> authentication.
          </p>
          <table class="table table-sm">
            <tr><th style="width: 30%">Server</th><td>{{ info.server }} <a href="#" (click)="copy(info.server); false"><span class="fas fa-copy"></span></a></td></tr>
            <tr><th>Database</th><td>{{ info.database }} <a href="#" (click)="copy(info.database); false"><span class="fas fa-copy"></span></a></td></tr>
            <tr *ngIf="!isSubscriptionLevel"><th>Schema</th><td>{{ info.servedSchema }} <a href="#" (click)="copy(info.servedSchema); false"><span class="fas fa-copy"></span></a></td></tr>
            <tr><th>Client (application) ID</th><td>{{ info.clientId }} <a href="#" (click)="copy(info.clientId); false"><span class="fas fa-copy"></span></a></td></tr>
            <tr>
              <th>Client secret</th>
              <td>
                {{ secretDisplay }}
                <a href="#" (click)="showSecret = !showSecret; false"><span class="fas" [ngClass]="showSecret ? 'fa-eye-slash' : 'fa-eye'" title="Show or hide"></span></a>&nbsp;
                <a href="#" (click)="copy(info.clientSecret); false"><span class="fas fa-copy" title="Copy"></span></a>&nbsp;
                <a href="#" (click)="rotateSecret(); false"><span class="fas fa-sync" title="Rotate secret"></span></a>
                <div class="text-muted" *ngIf="info.clientSecretExpiresUtc">Expires {{ info.clientSecretExpiresUtc + "Z" | date: "medium" }}</div>
              </td>
            </tr>
          </table>

          <ng-container *ngIf="isSubscriptionLevel && info.accessibleLayouts && info.accessibleLayouts.length > 0">
            <h4>Your Agent Data Models</h4>
            <p class="text-muted">Each one is a separate schema in the database above.</p>
            <table class="table table-sm">
              <tr><th>Name</th><th>Schema</th><th></th></tr>
              <tr *ngFor="let l of info.accessibleLayouts">
                <td>{{ l.name }}</td>
                <td>{{ l.servedSchema }}</td>
                <td class="text-muted">{{ l.materialized ? "" : "not generated yet" }}</td>
              </tr>
            </table>
          </ng-container>

          <ng-container *ngIf="info.snippets">
            <h4>Connection strings</h4>
            <div *ngFor="let s of [
              { label: 'ADO.NET', text: info.snippets.adoNet },
              { label: 'ODBC', text: info.snippets.odbc },
              { label: 'JDBC', text: info.snippets.jdbc },
              { label: 'Python (pyodbc)', text: info.snippets.pythonPyodbc }
            ]" style="margin-bottom: 10px">
              <b>{{ s.label }}</b> <a href="#" (click)="copy(s.text); false"><span class="fas fa-copy" title="Copy (includes the secret)"></span></a>
              <pre style="white-space: pre-wrap; word-break: break-all">{{ mask(s.text) }}</pre>
            </div>
          </ng-container>
        </div>
      </mat-tab>

      <mat-tab label="Tables" *ngIf="!isSubscriptionLevel && isAdmin">
        <div style="margin-top: 10px">
          <p class="text-muted" *ngIf="!info.tables || info.tables.length === 0">Nothing has been generated yet.</p>
          <p class="text-muted" *ngIf="info.instanceGeneratedUtc">Last refreshed {{ info.instanceGeneratedUtc + "Z" | date: "medium" }}</p>
          <div *ngFor="let t of info.tables" style="margin-bottom: 12px">
            <b>{{ t.tableName }}</b> <span class="text-muted">({{ t.rowCount | number }} rows)</span>
            <ul style="margin-bottom: 0">
              <li *ngFor="let c of t.columns">
                {{ c.outputName }} <span class="text-muted">{{ c.warehouseType }}<span *ngIf="c.originNote"> - from {{ c.originNote }}</span></span>
              </li>
            </ul>
          </div>
        </div>
      </mat-tab>
    </mat-tab-group>
  </ng-container>
</div>
<div mat-dialog-actions>
  <button mat-raised-button class="piButton" (click)="onClose()">Close</button>
</div>
